import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Button, Select, MenuItem, Tooltip, Chip, CircularProgress, Box, IconButton } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import { Link } from 'react-router-dom'
import { useAppStore } from '@/store/appStore'
import { OFFLINE_SESSION } from '@/utils/offlineSessionConfig'
import { runtimeEnv } from '@/utils/runtimeEnv'
import { mediaCachePercent, mediaCacheLabelParams } from '@/utils/mediaCacheProgress'
import { HelpMenu } from './HelpMenu'
import { HeaderOverflowMenu } from './HeaderOverflowMenu'
import { ThemeModeMenu } from './ThemeModeMenu'
import { fetchLanguages } from '@/api/languagesApi'
import { headerSelectSx } from '@/theme/muiTheme'
import { DARK_MODE_ENABLED } from '@/theme/themeMode'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useUpdateLanguagePreference } from '@/hooks/useUpdateLanguagePreference'
import { hasAdminPanelAccess } from '@/config/roles'
import { useAuthStore } from '@/stores/authStore'
import { useTour } from '@reactour/tour'
import { useLayoutMode, useIsBelowMd } from '@/hooks/useLayoutMode'

export function Header() {
  const { t, i18n } = useTranslation()
  const { data: currentUser } = useCurrentUser()
  const logout = useAuthStore((s) => s.logout)
  // Includes the read-only Analytics role: it reaches the panel, just a reduced view of it.
  const canOpenAdminPanel = hasAdminPanelAccess(currentUser?.roles)
  // Languages the user may draft-preview. The server folds the global DraftPreviewEnabled
  // killswitch, the bundle status and the user's roles into this list, so it empties out on its
  // own once any of them stops qualifying.
  const draftPreviewLanguages = currentUser?.draftPreviewLanguages
  const { setIsOpen, setCurrentStep } = useTour()
  const layoutMode = useLayoutMode()
  const isExpanded = layoutMode === 'expanded'
  const isCompact = layoutMode === 'compact'
  // Admin is blocked below `md`, so the entry point is not offered there either.
  const showAdminLink = !useIsBelowMd()
  const { mutate: persistLanguagePreference } = useUpdateLanguagePreference()

  const handleStartTour = () => {
    setCurrentStep(0)
    setIsOpen(true)
  }
  
  const {
    isOffline,
    isTrulyOffline,
    isManualOffline,
    setManualOffline,
    selectedLanguage,
    setSelectedLanguage,
    showDraftContent,
    setShowDraftContent,
    showContentDiff,
    setShowContentDiff,
    setOverlayPage,
    offlineSessionStart,
    imageCacheProgress,
    setMobileNavOpen,
    mobileNavOpen,
  } = useAppStore()

  // Draft preview is per-language: a bundle grants it only for the language it targets.
  const hasDraftPreviewAccess = draftPreviewLanguages?.includes(selectedLanguage) ?? false

  useEffect(() => {
    i18n.changeLanguage(selectedLanguage)
  }, [selectedLanguage, i18n])

  const [remainingMs, setRemainingMs] = useState<number | null>(null)

  useEffect(() => {
    if (!isOffline || offlineSessionStart === null) {
      setRemainingMs(null)
      return
    }
    const compute = () =>
      setRemainingMs(Math.max(0, OFFLINE_SESSION.maxDurationMs - (Date.now() - offlineSessionStart)))
    compute()
    const id = window.setInterval(compute, 1000)
    return () => window.clearInterval(id)
  }, [isOffline, offlineSessionStart])

  const { data: languagesData } = useQuery({
    queryKey: ['languages'],
    queryFn: fetchLanguages,
    staleTime: 60 * 60 * 1000,
  })

  // If the persisted selection is no longer available to this user (e.g. a hidden language whose
  // release bundle has since been published), fall back to the default language. Never runs while
  // offline, where the languages list is intentionally absent.
  useEffect(() => {
    const langs = languagesData?.languages
    if (isOffline || !langs?.length) return
    if (langs.some(l => l.code === selectedLanguage)) return
    const fallback = langs.find(l => l.isDefault)?.code ?? langs[0].code
    setSelectedLanguage(fallback)
  }, [languagesData, selectedLanguage, isOffline, setSelectedLanguage])

  // A hidden language reaches the selector only through draft preview, so there is no published
  // content to fall back to while it is selected.
  const languageRequiresDraft =
    hasDraftPreviewAccess &&
    !isOffline &&
    (languagesData?.languages.find(l => l.code === selectedLanguage)?.isDraftPreviewOnly ?? false)

  // Keep draft content on for such a language, and turn it back off once preview access for the
  // selected language is gone rather than leaving the bundle query to fail with a 403.
  useEffect(() => {
    if (!draftPreviewLanguages) return
    if (languageRequiresDraft && !showDraftContent) {
      setShowDraftContent(true)
      return
    }
    if (showDraftContent && !draftPreviewLanguages.includes(selectedLanguage)) {
      setShowDraftContent(false)
    }
  }, [draftPreviewLanguages, languageRequiresDraft, selectedLanguage, showDraftContent, setShowDraftContent])

  const handleOfflineToggle = () => {
    if (!isManualOffline) {
      setManualOffline(true)
    } else {
      if (!isTrulyOffline) {
        setManualOffline(false)
      }
    }
  }

  const handleLanguageChange = (code: string) => {
    setSelectedLanguage(code)
    // Persist to the user's profile so the choice follows them to new browsers/sessions.
    persistLanguagePreference(code)
  }

  const handleDraftToggle = () => {
    // If we're going into draft mode but the user just went offline, don't allow it.
    if (!showDraftContent && isOffline) return
    setShowDraftContent(!showDraftContent)
  }

  const goOnlineDisabled = isTrulyOffline

  const formatRemaining = (ms: number) => {
    const totalSeconds = Math.ceil(ms / 1000)
    const h = Math.floor(totalSeconds / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    const s = totalSeconds % 60
    if (h > 0) return t('offlineSession.remainingHours', { h, m })
    if (m > 0) return t('offlineSession.remainingMinutes', { m, s })
    return t('offlineSession.remainingSeconds', { s })
  }

  const offlineTooltip = (() => {
    if (!isOffline) return undefined
    const timerPart = remainingMs !== null ? formatRemaining(remainingMs) : null
    if (isTrulyOffline) {
      return timerPart
        ? t('offlineSession.cannotGoOnlineWithTimer', { timer: timerPart })
        : t('offlineSession.cannotGoOnline')
    }
    return timerPart ?? undefined
  })()
  // Draft toggle is disabled when offline (draft requires live connectivity) and when the selected
  // language has nothing but draft content to show.
  const draftToggleDisabled = (!showDraftContent && isOffline) || languageRequiresDraft
  const draftToggleTooltip = languageRequiresDraft
    ? t('draftPreview.languageRequiresDraft', 'This language is only available as draft preview.')
    : draftToggleDisabled
      ? t('draftPreview.offlineUnavailable')
      : ''

  return (
    <header className={`header${isOffline ? ' header--offline' : ''}${showDraftContent ? ' header--draft' : ''}`} role="banner">
      {!isExpanded && (
        <IconButton
          className="header-menu-btn"
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
          aria-label={mobileNavOpen ? t('header.closeNavigation') : t('header.openNavigation')}
          aria-expanded={mobileNavOpen}
          aria-controls="app-sidebar-drawer"
          sx={{ color: 'var(--color-white)', mr: 0.5, '&:hover': { backgroundColor: 'var(--color-on-dark-15)' } }}
        >
          <MenuIcon />
        </IconButton>
      )}

      <Link to="/" className="header-logo" data-tour="header-title" style={{ textDecoration: 'none', color: 'var(--color-white)' }}>
        <span style={{ position: 'relative', display: 'inline-flex', height: '110%', overflow: 'hidden', borderRadius: '15px' }}>
          <img src="/logos/logo-full-color-2025.svg" alt={t('header.logoAlt')} style={{ height: '100%' }} />
          {runtimeEnv.VITE_ENV_NAME && (
            <span
              aria-label={`Environment: ${runtimeEnv.VITE_ENV_NAME}`}
              style={{
                position: 'absolute',
                top: '7px',
                left: '-14px',
                width: '56px',
                transform: 'rotate(-45deg)',
                background: runtimeEnv.VITE_ENV_NAME === 'UAT'
                  ? 'var(--color-env-uat)'
                  : runtimeEnv.VITE_ENV_NAME === 'LOCAL' ? 'var(--color-env-local)' : 'var(--color-env-other)',
                color: 'var(--color-white)',
                fontSize: '8px',
                fontWeight: 700,
                letterSpacing: '0.03em',
                textAlign: 'center',
                lineHeight: '14px',
                pointerEvents: 'none',
                userSelect: 'none',
              }}
            >
              {runtimeEnv.VITE_ENV_NAME}
            </span>
          )}
        </span>
        <span>
          {/*{t('header.title')}*/}
          {isOffline && (
            <span className="header-offline-label" role="status" aria-live="polite">
              {' '}({t('header.currentlyOffline')})
            </span>
          )}
        </span>
      </Link>

      <div className="header-right">
        {/* Draft preview active indicator. At compact this becomes a dot on the overflow button. */}
        {showDraftContent && !isCompact && (
          <Chip
            label={t('draftPreview.activeIndicator')}
            size="small"
            role="status"
            aria-live="polite"
            sx={{
              alignSelf: 'center',
              backgroundColor: 'var(--color-highlight-a85)',
              color: 'var(--color-black)',
              fontWeight: 700,
              fontSize: '11px',
              height: '24px',
            }}
          />
        )}

        {/* Background image caching progress */}
        {!isCompact && imageCacheProgress !== null && imageCacheProgress.total > 0 && (
          <Tooltip
            title={t('header.cachingImagesProgress', mediaCacheLabelParams(imageCacheProgress))}
            placement="bottom"
          >
            <Box
              component="span"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                color: 'var(--color-on-dark-80)',
                fontSize: '11px',
                cursor: 'default',
                userSelect: 'none',
              }}
            >
              <CircularProgress
                size={14}
                thickness={5}
                variant="determinate"
                value={mediaCachePercent(imageCacheProgress)}
                sx={{ color: 'inherit', flexShrink: 0 }}
              />
              {imageCacheProgress.completed}/{imageCacheProgress.total}
            </Box>
          </Tooltip>
        )}

        {/* Draft Preview toggle — only rendered when the user has a qualifying preview role */}
        {hasDraftPreviewAccess && !isCompact && (
          <Tooltip title={draftToggleTooltip} placement="bottom">
            <span>
              <Button
                variant="outlined"
                onClick={handleDraftToggle}
                disabled={draftToggleDisabled}
                aria-pressed={showDraftContent}
                sx={{
                  color: showDraftContent ? 'var(--color-highlight)' : 'var(--color-white)',
                  borderColor: showDraftContent ? 'var(--color-highlight)' : 'var(--color-on-dark-60)',
                  '&:hover': {
                    borderColor: showDraftContent ? 'var(--color-highlight-light)' : 'var(--color-white)',
                    backgroundColor: 'var(--color-on-dark-10)',
                  },
                  '&.Mui-disabled': { color: 'var(--color-on-dark-40)', borderColor: 'var(--color-on-dark-25)' },
                }}
              >
                {showDraftContent ? t('draftPreview.toggleOff') : t('draftPreview.toggleOn')}
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Diff vs in-situ toggle — only meaningful (and visible) while draft preview is active */}
        {hasDraftPreviewAccess && showDraftContent && isExpanded && (
          <Tooltip title={t('draftPreview.diffToggleTooltip')} placement="bottom">
            <Button
              variant="outlined"
              onClick={() => setShowContentDiff(!showContentDiff)}
              aria-pressed={showContentDiff}
              sx={{
                color: showContentDiff ? 'var(--color-highlight)' : 'var(--color-white)',
                borderColor: showContentDiff ? 'var(--color-highlight)' : 'var(--color-on-dark-60)',
                '&:hover': {
                  borderColor: showContentDiff ? 'var(--color-highlight-light)' : 'var(--color-white)',
                  backgroundColor: 'var(--color-on-dark-10)',
                },
              }}
            >
              {showContentDiff ? t('draftPreview.diffToggleOff') : t('draftPreview.diffToggleOn')}
            </Button>
          </Tooltip>
        )}

        {!isCompact && (
        <Tooltip
          title={offlineTooltip}
          componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
        >
          <span>
            <Button
              className="offline-btn"
              variant="outlined"
              onClick={handleOfflineToggle}
              disabled={goOnlineDisabled}
              aria-pressed={isManualOffline}
              sx={{
                height: '100%',
                color: 'var(--color-white)',
                borderColor: 'var(--color-on-dark-60)',
                '&:hover': { borderColor: 'var(--color-white)', backgroundColor: 'var(--color-on-dark-10)' },
                '&.Mui-disabled': { color: 'var(--color-on-dark-40)', borderColor: 'var(--color-on-dark-25)' },
              }}
            >
              {isOffline ? t('header.goOnline') : t('header.goOffline')}
            </Button>
          </span>
        </Tooltip>
        )}

        <nav className="header-nav" aria-label={t('header.mainNavigation')}>
          {DARK_MODE_ENABLED && <ThemeModeMenu />}

          {!isCompact && (
          <Tooltip title={isOffline ? t('header.languageUnavailableOffline') : ''} placement="bottom">
            <span>
              <Select
                data-tour="language-select"
                value={selectedLanguage}
                onChange={(e) => handleLanguageChange(e.target.value)}
                inputProps={{ 'aria-label': t('header.selectLanguage') }}
                size="small"
                disabled={isOffline}
                sx={headerSelectSx}
              >
                {languagesData?.languages?.map((lang) => (
                  <MenuItem key={lang.id} value={lang.code} sx={{ fontSize: '13px' }}>
                    {lang.code.toUpperCase()}
                  </MenuItem>
                )) ?? <MenuItem value="en">EN</MenuItem>}
              </Select>
            </span>
          </Tooltip>
          )}

          {isExpanded && <a href={t('links.dashboard')}>{t('header.dashboard')}</a>}
          {!isCompact && (
            <HelpMenu
              onStartTour={handleStartTour}
              onOpenFaq={() => setOverlayPage({ type: 'faq' })}
              onOpenReleaseNotes={() => setOverlayPage({ type: 'release-notes' })}
              showTour={isExpanded}
            />
          )}
          {isExpanded && <a href="#" onClick={(e) => { e.preventDefault(); logout(); }}>{t('header.logOut')}</a>}
          {canOpenAdminPanel && isExpanded && (
            <Link
              to="/admin"
              style={{
                color: 'var(--color-white)',
                textDecoration: 'none',
                fontSize: '13px',
                padding: '4px 8px',
                borderRadius: '4px',
                background: 'var(--color-on-dark-15)',
                fontWeight: 600,
              }}
            >
              {t('header.admin')}
            </Link>
          )}
          {!isExpanded && (
            <HeaderOverflowMenu
              mode={layoutMode}
              canOpenAdminPanel={canOpenAdminPanel}
              showAdminLink={showAdminLink}
              hasDraftPreviewAccess={hasDraftPreviewAccess}
              showDraftContent={showDraftContent}
              draftToggleDisabled={draftToggleDisabled}
              onToggleDraft={handleDraftToggle}
              showContentDiff={showContentDiff}
              onToggleDiff={() => setShowContentDiff(!showContentDiff)}
              isOffline={isOffline}
              offlineDisabled={goOnlineDisabled}
              onToggleOffline={handleOfflineToggle}
              languages={languagesData?.languages ?? []}
              selectedLanguage={selectedLanguage}
              languageDisabled={isOffline}
              onLanguageChange={handleLanguageChange}
              imageCacheProgress={imageCacheProgress}
              onLogout={logout}
              onOpenFaq={() => setOverlayPage({ type: 'faq' })}
              onOpenReleaseNotes={() => setOverlayPage({ type: 'release-notes' })}
            />
          )}
        </nav>
      </div>
    </header>
  )
}
